import {
  ILayoutService,
  IPageLayoutSettings,
  PERMISSIONS,
  PermissionType,
  TRole,
  TViewItem,
  TViewMap,
  VIEW_MAP_TOKEN,
  USER_ROLE_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import {
  computed,
  effect,
  ElementRef,
  inject,
  Injectable,
  linkedSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { AssetService } from '../asset/asset.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { filter, map, mergeMap } from 'rxjs';

const applyToCollectionRule = /[#][^d]/g;

type ViewBP<View, ViewCategory> = {
  view: View | '*';
  categories: Array<ViewCategory>;
  tags: Array<string>;
};

type PermissionBP = {
  tag: string;
  applyToCollection?: boolean;
  claims: Array<{
    views?: Array<string>;
    roles: Array<TRole>;
    categories?: Array<string>;
    rights: PermissionType;
  }>;
};

@Injectable({
  providedIn: 'root',
})
export class LayoutService<
  ViewCategory,
  View,
  ViewItem extends TViewItem<unknown>,
  ViewMap extends TViewMap<ViewCategory, View>,
> implements ILayoutService<ViewCategory, View, ViewItem, ViewMap> {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly asset = inject(AssetService, { optional: true });
  private readonly viewMapToken = inject(VIEW_MAP_TOKEN) as ViewMap;
  private readonly user = inject(USER_ROLE_SERVICE_TOKEN);

  //#endregion

  //#region Constants

  private readonly ALL_VIEW_CATEGORIES = '*' as ViewCategory;

  //#endregion

  //#region Properties

  //Layout
  private readonly layoutResource = rxResource({
    stream: () => {
      return this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data),
      );
    },
  });
  readonly layoutSettings = computed<IPageLayoutSettings | undefined>(
    () => this.layoutResource.value()?.['masterLayoutSettings'],
  );
  readonly layoutIsReady = signal(false);

  //Views
  readonly viewCategory = signal<ViewCategory | undefined>(undefined);
  readonly viewMap = signal<ViewMap | undefined>(undefined);

  readonly availableViews = computed(
    () =>
      this.asset?.collection?.()?.get('views') as Array<
        ViewBP<View, ViewCategory>
      >,
  );
  readonly availableViewItems = computed(
    () => this.asset?.collection?.()?.get('items') as Array<ViewItem>,
  );

  readonly filteredViews = computed<Array<View> | undefined>(() =>
    this.viewCategory()
      ? this.viewMapToken?.find(
          (viewMapItem) => viewMapItem.viewCategory === this.viewCategory(),
        )!.views
      : undefined,
  );
  readonly defaultView = signal<View | undefined>(undefined);
  readonly selectedView = signal<View | undefined>(undefined);
  readonly selectedViewItems = signal<Array<ViewItem> | undefined>(undefined);

  //Permissions
  readonly permissions = linkedSignal(
    () => this.asset?.collection?.()?.get('permissions') as Array<PermissionBP>,
  );
  readonly togglePermissions = signal(false);
  readonly editPermissionsForControls = signal<string | undefined>(undefined);
  readonly editingPermissionTag = signal<string | undefined>(undefined);
  readonly editingPermission = signal<PermissionBP | undefined>(undefined);
  readonly editingClaims = signal<
    | Array<{
        isCollection?: boolean;
        views?: Array<string>;
        roles: Array<TRole>;
        rights: PermissionType;
      }>
    | undefined
  >([]);

  //Anchors
  anchorhost!: ElementRef;
  anchors: WritableSignal<
    Array<{ tag: string; ref: ElementRef; offset: number }>
  > = signal([]);

  //#endregion

  //#region Methods

  constructor() {
    effect(() => {
      if (
        this.viewCategory() &&
        this.selectedView() &&
        this.availableViews() &&
        this.availableViewItems() &&
        this.permissions()
      ) {
        this.updateItems();
      }
    });
    effect(() => {
      if (this.layoutIsReady() === false) {
        this.clearAnchors();
      }
    });
  }

  //Views
  selectView(view: View) {
    this.clearAnchors();
    this.selectedView.set(view);
  }

  setDefaultView(view: View, store = true) {
    this.defaultView.set(view);
    if (store) {
      this.user.saveUserPreferences({ defaultView: view as string });
    }
  }

  downloadView() {
    console.log(this.selectedView());
  }

  getViewItem(
    path: string | Array<string>,
    viewItems: Array<ViewItem>,
  ): ViewItem | undefined {
    if (typeof path === 'string') {
      path = path.split('/');
    }

    if (!path.length) {
      return undefined;
    }
    const [current, ...rest] = path;
    const foundItem = viewItems.find((item) => item.settings.tag === current);
    if (foundItem) {
      if (rest.length === 0) {
        return foundItem;
      } else if (foundItem.content) {
        return this.getViewItem(rest, foundItem.content as Array<ViewItem>);
      }
    }
    return undefined;
  }

  private updateItems() {
    if (this.selectedView()) {
      let viewTags = this.availableViews()?.find(
        (view) =>
          (view.view === this.selectedView() || view.view === '*') &&
          (view.categories.includes(this.viewCategory()!) ||
            view.categories.includes(this.ALL_VIEW_CATEGORIES)),
      )?.tags;

      let viewItems: Array<ViewItem> = [];
      viewTags?.forEach((tag) => {
        let item = this.availableViewItems()?.find(
          (item) => item.settings.tag === tag,
        );
        if (item) {
          viewItems.push({ ...item });
        }
      });
      this.selectedViewItems.set(viewItems);
    } else {
      this.selectedViewItems.set([]);
    }
  }

  //Permissions
  getPermissionFor(control: string, tag: string) {
    let claims = this.permissions()?.find((permission) =>
      tag?.includes('#') && permission.applyToCollection
        ? permission.tag ===
          `${control}|${tag.replace(applyToCollectionRule, '#')}`
        : permission.tag === `${control}|${tag}`,
    )?.claims;
    let rights = claims?.find(
      (claim) =>
        (claim.views?.includes(this.selectedView() as string) ||
          !claim.views ||
          claim.views?.length === 0) &&
        (claim.categories?.includes(this.viewCategory() as string) ||
          !claim.categories ||
          claim.categories?.length === 0) &&
        claim.roles?.some((role) => this.user.isInRole(role)),
    )?.rights;
    return rights;
  }

  editPermissionFor(control: string, tag: string) {
    this.editingPermissionTag.set(`${control}|${tag}`);
    let permission = this.permissions()?.find((permission) =>
      tag?.includes('#') && permission.applyToCollection
        ? permission.tag ===
          this.editingPermissionTag()?.replace(applyToCollectionRule, '#')
        : permission.tag === this.editingPermissionTag(),
    );
    this.editingPermission.set(permission);
  }

  applyClaimToCollection() {
    this.editingPermission.update((permission) => {
      permission!.tag = !permission?.applyToCollection
        ? this.editingPermissionTag()!
        : this.editingPermissionTag()!.replace(applyToCollectionRule, '#');
      return permission;
    });
  }

  addClaim() {
    if (!this.editingPermission()) {
      this.editingPermission.set({
        tag: this.editingPermissionTag()!,
        applyToCollection: false,
        claims: [
          {
            views: [],
            roles: [],
            categories: [],
            rights: PERMISSIONS.WRITE,
          },
        ],
      });
      this.permissions.update((permissions) => {
        permissions!.push(this.editingPermission()!);
        return permissions;
      });
    } else {
      this.editingPermission.update((permission) => {
        permission!.claims = [
          ...(permission!.claims ?? []),
          { roles: [], views: [], rights: PERMISSIONS.WRITE },
        ];

        return permission;
      });
    }
  }

  removeClaim(index: number) {
    this.editingPermission.update((permission) => {
      permission?.claims.splice(index, 1);
      return permission;
    });

    if (this.editingPermission()?.claims.length === 0) {
      this.permissions.update((permissions) => {
        permissions!.splice(
          permissions!.findIndex(
            (permission) => permission.tag === this.editingPermissionTag(),
          ),
          1,
        );
        return permissions;
      });
      this.editingPermission.set(undefined);
    }
  }

  downloadPermissions() {
    const jsonString = JSON.stringify(this.permissions());
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'PERMISSIONS.json';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  //Anchors
  addAnchorhost(host: ElementRef) {
    this.anchorhost = host;
  }

  addAnchor(tag: string, ref: ElementRef, offset = 0) {
    let anchors = this.anchors();
    if (!anchors.some((a) => a.tag === tag)) {
      anchors.push({ tag, ref, offset });
    }
    this.anchors.set(anchors);
  }

  clearAnchors() {
    this.anchors.set([]);
  }

  refreshAnchors() {
    this.anchors.update((anchors) => {
      return [...(anchors ?? [])];
    });
  }

  public jumpToAnchor(anchorEl?: ElementRef, offset = 0) {
    if (!anchorEl) {
      this.anchorhost?.nativeElement?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (anchorEl) {
      this.anchorhost?.nativeElement?.scrollTo({
        top: anchorEl.nativeElement.offsetTop - offset,
        behavior: 'smooth',
      });
    }
  }

  //#endregion
}
